import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { getAlbaran, deleteAlbaran } from "../lib/storage";
import { downloadAlbaranPDF } from "../lib/pdfAlbaran";
import { AlbaranDocument } from "../components/AlbaranDocument";
import { ArrowLeft, Trash2, Pencil, FileDown, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function AlbaranDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [albaran, setAlbaran] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  useEffect(() => {
    setAlbaran(getAlbaran(id));
    setLoading(false);
  }, [id]);

  const handleDelete = () => {
    if (!window.confirm("¿Eliminar este albarán? Esta acción no se puede deshacer.")) return;
    setDeleting(true);
    deleteAlbaran(id);
    toast.success("Albarán eliminado");
    navigate("/");
  };

  const handlePdf = async () => {
    setPdfBusy(true);
    try {
      await downloadAlbaranPDF(albaran);
      toast.success("PDF generado");
    } catch (e) {
      toast.error("No se pudo generar el PDF.");
    } finally {
      setPdfBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 flex justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!albaran) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <p className="text-slate-600">Albarán no encontrado.</p>
        <Link to="/" className="text-blue-600 font-semibold mt-2 inline-block">Volver a la lista</Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 md:py-10">
      <div className="flex items-center justify-between gap-2 mb-6 flex-wrap">
        <button
          onClick={() => navigate("/")}
          data-testid="back-btn"
          className="inline-flex items-center gap-1.5 h-10 px-3 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-100 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" /> Volver
        </button>
        <div className="flex items-center gap-2">
          <button
            onClick={handleDelete}
            disabled={deleting}
            data-testid="delete-albaran-btn"
            className="inline-flex items-center gap-1.5 h-11 px-4 rounded-lg text-sm font-semibold text-red-600 border border-red-200 hover:bg-red-50 disabled:opacity-60 transition-colors"
          >
            {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            <span className="hidden sm:inline">Eliminar</span>
          </button>
          <Link
            to={`/albaran/${albaran.id}/editar`}
            data-testid="edit-albaran-btn"
            className="inline-flex items-center gap-1.5 h-11 px-4 rounded-lg text-sm font-semibold text-slate-700 border border-slate-300 hover:bg-slate-50 transition-colors"
          >
            <Pencil className="h-4 w-4" /> <span className="hidden sm:inline">Editar</span>
          </Link>
          <button
            onClick={handlePdf}
            disabled={pdfBusy}
            data-testid="download-pdf-btn"
            className="inline-flex items-center gap-1.5 h-11 px-5 rounded-lg text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 transition-colors"
          >
            {pdfBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
            Exportar PDF
          </button>
        </div>
      </div>

      <AlbaranDocument albaran={albaran} />
    </div>
  );
}
